/**
 * Plaid Link, the provider's hosted institution picker.
 *
 * This surface does not draw the picker and does not know which institutions
 * exist — it hands Link a short-lived link token from the Server and gets back
 * a public token, which goes straight to `POST /plaid/exchange`. The durable
 * access token is created and encrypted server-side and is never in this
 * process. Nothing here is persisted: no token touches localStorage.
 *
 * The script is loaded on demand rather than in index.html so that a surface
 * with linking switched off never talks to the provider's CDN at all.
 */

const SCRIPT_SRC = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js'

/** The slice of Link's API this surface uses. */
interface PlaidHandler {
  open: () => void
  exit: (options?: { force?: boolean }) => void
  destroy: () => void
}

interface PlaidLinkOnExitError {
  error_type?: string
  error_code?: string
  display_message?: string | null
}

interface PlaidFactory {
  create: (config: {
    token: string
    onSuccess: (publicToken: string, metadata: unknown) => void
    onExit: (error: PlaidLinkOnExitError | null, metadata: unknown) => void
    onLoad?: () => void
  }) => PlaidHandler
}

declare global {
  interface Window {
    Plaid?: PlaidFactory
  }
}

/**
 * Link could not be reached at all: no script, so no picker, so no connection.
 * This surfaces as `linkAccount.error.unavailable` — the same answer the Server
 * gives when the provider is not configured. A blocked CDN and an unconfigured
 * Server are indistinguishable to the person at the keyboard, so they get the
 * same message and the same fallback to statement upload.
 */
export class LinkUnavailableError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'LinkUnavailableError'
  }
}

let loader: Promise<PlaidFactory> | null = null

/** Loads link-initialize.js once per page and resolves the global it defines. */
function loadLink(): Promise<PlaidFactory> {
  if (window.Plaid) return Promise.resolve(window.Plaid)
  if (loader) return loader

  loader = new Promise<PlaidFactory>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.addEventListener('load', () => {
      if (window.Plaid) resolve(window.Plaid)
      else reject(new LinkUnavailableError('Plaid Link loaded without defining window.Plaid'))
    })
    script.addEventListener('error', () =>
      reject(new LinkUnavailableError('Plaid Link script failed to load')),
    )
    document.head.appendChild(script)
  })
  // A failed load must not poison every later attempt — Try again should retry.
  // The dead tag goes with it: adding a listener to a script that already fired
  // would wait for an event that never comes again, hanging the retry forever.
  loader.catch(() => {
    document.querySelector(`script[src="${SCRIPT_SRC}"]`)?.remove()
    loader = null
  })
  return loader
}

/**
 * The three ways Link can end. `cancelled` is the user backing out, which is a
 * normal outcome and gets its own copy (`linkError.cancelled`) rather than
 * being dressed up as a failure.
 */
export type LinkResult =
  | { outcome: 'success'; publicToken: string }
  | { outcome: 'cancelled' }
  | { outcome: 'failed'; code: string | null }

/**
 * The dev mock backend cannot issue a real link token, and Link would reject
 * one. Tokens it mints carry a sentinel prefix so the whole flow stays walkable
 * without contacting the provider — see installMock in lib/mock/transport.ts.
 * Production tokens never match, and the branch is dropped from the bundle.
 */
const MOCK_PREFIX = 'mock-link-'

function mockOutcome(linkToken: string): LinkResult {
  const outcome = linkToken.slice(MOCK_PREFIX.length)
  if (outcome === 'cancelled') return { outcome: 'cancelled' }
  if (outcome === 'failed') return { outcome: 'failed', code: 'INSTITUTION_DOWN' }
  return { outcome: 'success', publicToken: `mock-public-${outcome}` }
}

/**
 * Opens Link and resolves once the user finishes or backs out. Rejects only if
 * the script itself could not load — that is a `linkAccount.error.title` case,
 * not a `linkError` one, because the connection never started.
 */
export async function openLink(linkToken: string): Promise<LinkResult> {
  if (import.meta.env.DEV && linkToken.startsWith(MOCK_PREFIX)) {
    await new Promise((resolve) => setTimeout(resolve, 400))
    return mockOutcome(linkToken)
  }

  const Plaid = await loadLink()
  return new Promise<LinkResult>((resolve) => {
    let settled = false
    const finish = (result: LinkResult) => {
      if (settled) return
      settled = true
      // Let Link finish its own teardown before we drop the handler.
      setTimeout(() => handler.destroy(), 0)
      resolve(result)
    }

    const handler = Plaid.create({
      token: linkToken,
      onSuccess: (publicToken) => finish({ outcome: 'success', publicToken }),
      onExit: (error) => {
        // Link reports a clean user exit as a null error.
        if (!error) return finish({ outcome: 'cancelled' })
        finish({ outcome: 'failed', code: error.error_code ?? error.error_type ?? null })
      },
    })

    handler.open()
  })
}
